import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { CoutSaisie } from '../../types.js';
import { CelluleMontant, EtatOnglet, formaterMois, useLangue, useMessageErreur } from './commun.js';
import { TableauSaisie, type ChampSaisie } from './TableauSaisie.js';
import { useSaisies } from './useSaisies.js';

/** Les coûts du mois, poste par poste (direction) : la marge de la vue d'aigle en est tirée. */
export function CoutsTab({ enEcriture }: { enEcriture: boolean }): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  const message = useMessageErreur('couts');
  const { lignes, chargement, enErreur, creer, modifier, supprimer } = useSaisies('couts');

  const champs: ChampSaisie[] = [
    { cle: 'mois', titre: t('finances.couts.champs.mois'), type: 'mois', requis: true },
    {
      cle: 'poste',
      titre: t('finances.couts.champs.poste'),
      type: 'texte',
      requis: true,
      longueurMax: 80,
      aide: t('saisies.visibleAssocies'),
    },
    { cle: 'montantCentimes', titre: t('finances.couts.champs.montant'), type: 'montant' },
  ];

  if (chargement || enErreur) return <EtatOnglet chargement={chargement} enErreur={enErreur} />;
  return (
    <TableauSaisie<CoutSaisie>
      legende={t('finances.onglets.couts')}
      introduction={t('finances.couts.texte')}
      lignes={lignes}
      colonnes={[
        {
          cle: 'mois',
          titre: t('finances.couts.colonnes.mois'),
          rendu: (ligne) => formaterMois(ligne.mois, langue),
        },
        { cle: 'poste', titre: t('finances.couts.colonnes.poste'), rendu: (ligne) => ligne.poste },
        {
          cle: 'montant',
          titre: t('finances.couts.colonnes.montant'),
          numerique: true,
          rendu: (ligne) => <CelluleMontant centimes={ligne.montantCentimes} />,
        },
      ]}
      champs={champs}
      valeursDe={(ligne) => ({
        mois: ligne.mois,
        poste: ligne.poste,
        montantCentimes: ligne.montantCentimes,
      })}
      libelleLigne={(ligne) => ligne.poste}
      enEcriture={enEcriture}
      libelleAjouter={t('finances.couts.ajouter')}
      messageVide={t('finances.couts.vide')}
      sansMontant={lignes.filter((ligne) => ligne.montantCentimes === null).length}
      surCreer={creer}
      surModifier={(ligne, changements) => modifier(ligne.id, changements)}
      surSupprimer={(ligne) => supprimer(ligne.id)}
      message={message}
    />
  );
}
